import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import ImageManager from '../../components/admin/ImageManager';
import {
  API, withCreds, inputCls, selectCls, btnPrimary, btnSecondary, btnDanger,
  Field, Modal, PageHeader, Spinner, errMsg,
} from '../../components/admin/AdminUI';
import { Plus, Edit, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, Layers, GitMerge, ListPlus } from 'lucide-react';
import Select from '../../components/FlatSelect';

function CategoryModal({ category, onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', description: '', image: '', active: true, ...(category || {}) });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const body = { name: form.name, description: form.description, image: form.image, active: form.active };
    try {
      if (category) await axios.put(`${API}/admin/categories/${category.id}`, body, withCreds);
      else await axios.post(`${API}/admin/categories`, body, withCreds);
      onSaved();
    } catch (err) { setError(errMsg(err, 'Save failed')); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={category ? 'Edit Category' : 'New Category'} onClose={onClose} width="max-w-lg">
      <form onSubmit={save} className="space-y-4">
        <Field label="Name *" hint={category ? 'Renaming updates every product in this category' : ''}>
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required className={inputCls} />
        </Field>
        <Field label="Description">
          <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} className={`${inputCls} resize-none`} />
        </Field>
        <Field label="Image" hint="Optional. If empty, the first product photo in the category is used.">
          <ImageManager images={form.image ? [form.image] : []} onChange={imgs => setForm({ ...form, image: imgs[imgs.length - 1] || '' })} multiple={false} subfolder="categories" />
        </Field>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-honey" />
          <span className="text-ink/75 text-sm">Visible in the store</span>
        </label>
        {error && <p className="text-red-600 text-sm p-3 bg-red-500/10 rounded-xl">{error}</p>}
        <div className="flex gap-3">
          <button type="submit" disabled={saving} className={`${btnPrimary} flex-1`}>{saving ? 'Saving…' : 'Save'}</button>
          <button type="button" onClick={onClose} className={btnSecondary}>Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

function BulkAddModal({ onClose, onSaved }) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const names = text.split(/\n|,/).map(n => n.trim()).filter(Boolean);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/admin/categories/bulk-create`, { names }, withCreds);
      alert(`Created ${r.data.created} categor${r.data.created === 1 ? 'y' : 'ies'}${r.data.created < names.length ? ' (existing names were skipped)' : ''}.`);
      onSaved();
    } catch (e) { alert(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Add several categories" onClose={onClose} width="max-w-lg">
      <div className="space-y-4">
        <Field label="Category names" hint="One per line (or comma separated). Names that already exist are skipped.">
          <textarea value={text} onChange={e => setText(e.target.value)} rows={8} placeholder={'Kitchen\nHome Decor\nOutdoor'} className={`${inputCls} resize-y`} />
        </Field>
        <button onClick={save} disabled={!names.length || saving} className={`${btnPrimary} w-full`}>{saving ? 'Adding…' : `Add ${names.length} categor${names.length === 1 ? 'y' : 'ies'}`}</button>
      </div>
    </Modal>
  );
}

function DeleteModal({ category, categories, onClose, onDone }) {
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const others = categories.filter(c => c.id !== category.id);

  const remove = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/categories/${category.id}`, { params: { reassign_to: target }, ...withCreds });
      onDone();
    } catch (e) { alert(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={`Delete "${category.name}"`} onClose={onClose} width="max-w-md">
      <div className="space-y-4">
        {category.product_count > 0 ? (
          <Field label={`Move its ${category.product_count} product(s) to`}>
            <Select value={target} onChange={e => setTarget(e.target.value)} className={`${selectCls} w-full`}>
              <option value="">No category (uncategorized)</option>
              {others.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </Select>
          </Field>
        ) : <p className="text-ink/60 text-sm">This category has no products.</p>}
        <div className="flex gap-3">
          <button onClick={remove} disabled={busy} className={`${btnDanger} flex-1`}><Trash2 className="w-4 h-4" /> Delete category</button>
          <button onClick={onClose} className={btnSecondary}>Cancel</button>
        </div>
      </div>
    </Modal>
  );
}

function MergeModal({ selected, categories, onClose, onDone }) {
  const chosen = categories.filter(c => selected.includes(c.id));
  const [target, setTarget] = useState(chosen[0]?.id || '');
  const [busy, setBusy] = useState(false);

  const merge = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/categories/merge`, { target_id: target, source_ids: selected.filter(id => id !== target) }, withCreds);
      alert(`Moved ${r.data.moved_products} product(s) and removed ${r.data.removed_categories} categor${r.data.removed_categories === 1 ? 'y' : 'ies'}.`);
      onDone();
    } catch (e) { alert(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={`Merge ${chosen.length} categories`} onClose={onClose} width="max-w-md">
      <div className="space-y-4">
        <Field label="Keep this one" hint="Products from the others move into it, then the others are deleted.">
          <Select value={target} onChange={e => setTarget(e.target.value)} className={`${selectCls} w-full`}>
            {chosen.map(c => <option key={c.id} value={c.id}>{c.name} ({c.product_count})</option>)}
          </Select>
        </Field>
        <button onClick={merge} disabled={busy || chosen.length < 2} className={`${btnPrimary} w-full`}><GitMerge className="w-4 h-4" /> Merge</button>
      </div>
    </Modal>
  );
}

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // category | 'new' | null
  const [showBulk, setShowBulk] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [selected, setSelected] = useState([]);
  const [showMerge, setShowMerge] = useState(false);

  const load = async () => {
    try { setCategories((await axios.get(`${API}/admin/categories`, withCreds)).data.categories); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const closeAll = () => { setEditing(null); setShowBulk(false); setDeleting(null); setShowMerge(false); setSelected([]); load(); };

  const move = async (index, dir) => {
    const next = [...categories];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    setCategories(next);
    await axios.post(`${API}/admin/categories/reorder`, { ids: next.map(c => c.id) }, withCreds);
  };

  const toggleActive = async (c) => {
    await axios.put(`${API}/admin/categories/${c.id}`, { active: !c.active }, withCreds);
    load();
  };

  const toggleSelect = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const uncategorizedHint = 'Products with no category still show under "All" in the store.';

  return (
    <AdminLayout>
      {editing && <CategoryModal category={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={closeAll} />}
      {showBulk && <BulkAddModal onClose={() => setShowBulk(false)} onSaved={closeAll} />}
      {deleting && <DeleteModal category={deleting} categories={categories} onClose={() => setDeleting(null)} onDone={closeAll} />}
      {showMerge && <MergeModal selected={selected} categories={categories} onClose={() => setShowMerge(false)} onDone={closeAll} />}

      <PageHeader title="Categories" subtitle={`${categories.length} categories · the order here is the order shoppers see`}>
        {selected.length >= 2 && <button onClick={() => setShowMerge(true)} className={btnSecondary}><GitMerge className="w-4 h-4" /> Merge {selected.length}</button>}
        <button onClick={() => setShowBulk(true)} className={btnSecondary}><ListPlus className="w-4 h-4" /> Add several</button>
        <button onClick={() => setEditing('new')} className={btnPrimary}><Plus className="w-4 h-4" /> New Category</button>
      </PageHeader>

      <div className="admin-card rounded-xl overflow-hidden">
        {loading ? <Spinner /> : categories.length === 0 ? (
          <div className="text-center py-12">
            <Layers className="w-8 h-8 text-ink/30 mx-auto mb-2" />
            <p className="text-ink/45 text-sm">No categories yet.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-ink/10">
              <tr className="text-ink/55 text-xs uppercase tracking-widest">
                <th className="p-3 w-8"></th>
                <th className="text-left p-3">Category</th>
                <th className="text-left p-3">Products</th>
                <th className="text-left p-3">Visibility</th>
                <th className="text-left p-3">Order</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink/10">
              {categories.map((c, i) => (
                <tr key={c.id} className="text-ink/75 hover:bg-ink/[0.04]">
                  <td className="p-3"><input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggleSelect(c.id)} className="accent-honey" /></td>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-ink/[0.04] flex-shrink-0">
                        {c.image ? <img src={c.image} alt="" className="w-full h-full object-cover" /> : <Layers className="w-5 h-5 text-ink/30 m-auto mt-2.5" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-ink font-medium">{c.name}</p>
                        {c.description && <p className="text-ink/45 text-xs truncate max-w-[320px]">{c.description}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="p-3">
                    <Link to={`/admin/products?category=${encodeURIComponent(c.name)}`} className="text-honey-700 hover:underline">{c.product_count}</Link>
                  </td>
                  <td className="p-3">
                    <button onClick={() => toggleActive(c)} className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full font-bold ${c.active ? 'bg-green-500/10 text-green-600' : 'bg-ink/[0.04] text-ink/45'}`}>
                      {c.active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />} {c.active ? 'Visible' : 'Hidden'}
                    </button>
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-ink/55 hover:text-ink disabled:opacity-20"><ArrowUp className="w-4 h-4" /></button>
                      <button onClick={() => move(i, 1)} disabled={i === categories.length - 1} className="p-1 text-ink/55 hover:text-ink disabled:opacity-20"><ArrowDown className="w-4 h-4" /></button>
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2 justify-end">
                      <button onClick={() => setEditing(c)} className="text-ink/55 hover:text-honey-700"><Edit className="w-4 h-4" /></button>
                      <button onClick={() => setDeleting(c)} className="text-ink/55 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p className="text-ink/45 text-xs mt-3">{uncategorizedHint} Categories only appear in the store once they contain a published product.</p>
    </AdminLayout>
  );
}
